import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { Avatar, FetchAPI, FormAPI, PanelAPI, ToastNotificationAPI, UnboxedAutocompleteAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationQualificationsAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

interface Response {
  response: string,
  status: string,
}

interface Qualification {
  dsAccreditation: string,
  dsAccreditationType: string,
  dsAccreditationLevel: string,
  nrSeqQualification: number,
  ieAccreditationLevel: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: FragmentNames.PROVIDER_ACCREDITATION_QUALIFICATIONS })
export default class ProviderAccreditationQualifications extends BaseController {

  @Inject(Aliases.FETCH_QUALIFICATIONS)
  private fetchQualifications: FetchAPI;
  @Inject(Aliases.FORM_QUALIFICATION)
  private formQualification: FormAPI;
  @Inject(Aliases.FETCH_FORM)
  private fetchForm: FetchAPI;
  @Inject(Aliases.UNBOXED_AUTOCOMPLETE_LEVEL)
  private autocompleteAccreditationLevel: UnboxedAutocompleteAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;

  @OnMount(Aliases.PANEL_FORM)
  onMountAddButtom(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.QUALIFICATIONS);
  }

  @OnMount(Aliases.PANEL_ACCREDITATION_QUALIFICATIONS)
  onMountMainPanel(): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqUser, nrSeqAccreditation });
  }

  @OnChange(Aliases.UNBOXED_AUTOCOMPLETE_ACCREDITATION)
  onchangeAutocomplete(event: ChangeEvent<any>): void {
    if (event.value == 'AONA') {
      this.autocompleteAccreditationLevel.setEnabled(true);
    } else {
      this.autocompleteAccreditationLevel.setEnabled(false);
    }
  }

  @OnSubmit(Aliases.FORM_QUALIFICATION)
  onSubmit(event: SubmitEvent): void {
    const body = event.record;
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void HttpService.post(Endpoint.POST.QUALIFICATION, { nrSeqUser, nrSeqAccreditation }, { body }).then(() => {
      this.cleanForm();
      this.fetchQualifications.forceFetch();
    }).catch((error) => {
      const { response } = error.body as unknown as Response;
      ToastServiceProviderAccreditation.error(response);
    });

  }

  @OnDynamicContentRender(Aliases.AVATAR)
  onMountCard(event: RenderEvent): void {
    const { render } = event;
    const { dsAccreditation, dsAccreditationType, dsAccreditationLevel } = event.data as Qualification;
    const level = dsAccreditationLevel ? `${MessageService.translate('pa.level')} ${dsAccreditationLevel}` : '';
    render((): ReactElement => <Avatar
      hideImage={true}
      title={dsAccreditation}
      subtitle={dsAccreditationType}
      description={level} />);
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable } = data;

    const show = {
      [OptionKey.EDIT]: editable == 'S',
      [OptionKey.DELETE]: editable == 'S',
    };

    shouldRender(show[key]);
  }

  @HPMSOnCreateMenu(Aliases.HPMS_CUSTOM_CARD)
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;

    if (editable == 'N') {
      shouldRender(false);
    }
  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, any>>): void {
    const { data, key } = event;
    const { nrSeqQualification } = data as Qualification;
    const action = {
      [OptionKey.EDIT]: () => this.editRecord(nrSeqQualification),
      [OptionKey.DELETE]: () => this.deleteRecord(nrSeqQualification),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnCancel(Aliases.FORM_QUALIFICATION)
  onCancelForm(): void {
    this.cleanForm();
  }

  @OnAfterFetch(Aliases.FETCH_FORM)
  onAfterFetchForm(event: FetchEvent): void {
    if (event.data) {
      const { ieAccreditationLevel } = event.data as Qualification;
      if (ieAccreditationLevel) {
        this.autocompleteAccreditationLevel.setEnabled(true);
      }
    }
  }

  private editRecord(nrSeqQualification: number): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.fetchForm.setParameters({ nrSeqUser, nrSeqAccreditation, nrSeqQualification });
    this.fetchForm.forceFetch();
  }

  private deleteRecord(nrSeqQualification: number): void {
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void HttpService.delete(Endpoint.DELETE.QUALIFICATION, { nrSeqQualification, nrSeqUser, nrSeqAccreditation }).then(() => {
      this.fetchQualifications.forceFetch();
    }).catch((error) => {
      const { response } = error.body as unknown as Response;
      ToastServiceProviderAccreditation.error(response);
    });
  }

  private cleanForm(): void {
    this.formQualification.setRecord({});
    this.formQualification.setData({});
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.QUALIFICATIONS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

}
